// src/features/store/components/StoreSelector.tsx

import React, {
  useEffect,
  useState,
} from 'react';

import {
  storeService,
} from '../services/storeService';

import type {
  StoreListItem,
} from '../types/store.types';

interface StoreSelectorProps {
  selectedStoreId: number | null;

  onStoreChange: (
    storeId: number,
  ) => void;

  label?: string;
}

const StoreSelector:
  React.FC<StoreSelectorProps> = ({
    selectedStoreId,
    onStoreChange,
    label = '지점 선택',
  }) => {
  const [
    stores,
    setStores,
  ] =
    useState<StoreListItem[]>(
      [],
    );

  const [
    loading,
    setLoading,
  ] =
    useState<boolean>(
      true,
    );

  /**
   * 매장 목록 조회
   */
  useEffect(() => {
    let isActive =
      true;

    const loadStores =
      async (): Promise<void> => {
        try {
          setLoading(
            true,
          );

          const data =
            await storeService.getStores();

          if (!isActive) {
            return;
          }

          /**
           * storeId = 0 제외가 필요하다면
           * 여기서 처리
           */
          const filteredStores =
            data.filter(
              (
                store:
                  StoreListItem,
              ) =>
                store.storeId !==
                0,
            );

          setStores(
            filteredStores,
          );

          /**
           * 선택된 매장이 없다면
           * 첫 번째 매장 자동 선택
           */
          if (
            selectedStoreId ===
              null &&
            filteredStores.length >
              0
          ) {
            onStoreChange(
              filteredStores[0]
                .storeId,
            );
          }
        } catch (
          error: unknown
        ) {
          console.error(
            '매장 목록 조회 실패:',
            error,
          );

          if (
            isActive
          ) {
            setStores(
              [],
            );
          }
        } finally {
          if (
            isActive
          ) {
            setLoading(
              false,
            );
          }
        }
      };

    void loadStores();

    return () => {
      isActive = false;
    };
  }, [
    selectedStoreId,
    onStoreChange,
  ]);

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        {label && (
          <label className="text-16-medium text-gray-700">
            {label}:
          </label>
        )}

        <select
          disabled
          className="rounded-lg border border-gray-300 bg-gray-100 px-4 py-2 text-16-regular text-gray-500"
        >
          <option>
            로딩 중...
          </option>
        </select>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {label && (
        <label className="text-16-medium text-gray-700">
          {label}:
        </label>
      )}

      <select
        value={
          selectedStoreId ??
          ''
        }
        onChange={(
          event,
        ) => {
          const storeId =
            Number(
              event.target
                .value,
            );

          onStoreChange(
            storeId,
          );
        }}
        className="rounded-lg border border-gray-300 px-4 py-2 text-16-regular outline-none focus:border-main"
      >
        <option
          value=""
          disabled
        >
          지점을 선택하세요
        </option>

        {stores.map(
          (
            store:
              StoreListItem,
          ) => (
            <option
              key={
                store.storeId
              }
              value={
                store.storeId
              }
            >
              {
                store.storeName
              }
            </option>
          ),
        )}
      </select>
    </div>
  );
};

export default StoreSelector;